import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../API/api';
import Joi from 'joi';

const supportRequestSchema = Joi.object({
  user_id: Joi.string().required(),
  subject: Joi.string().required(),
  description: Joi.string().required(),
  initial_message: Joi.string().required(),
});

// Thunks الموجودة
export const fetchSupportRequests = createAsyncThunk('support/fetchSupportRequests', async (userId) => {
  const response = await api.get(`/support/requests/${userId}`);
  return response.data;
});

export const createSupportRequest = createAsyncThunk('support/createSupportRequest', async (data, { rejectWithValue }) => {
  try {
    const response = await api.post(`/support/request`, data);
    return { supportRequest: response.data.supportRequest, autoReply: response.data.auto_reply };
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Failed to create support request');
  }
});

export const fetchSupportSessions = createAsyncThunk('support/fetchSupportSessions', async (userId) => {
  const response = await api.get(`/support/sessions/${userId}`);
  return response.data;
});

export const closeSupportSession = createAsyncThunk('support/closeSupportSession', async ({ sessionId, rating }) => {
  const response = await api.put(`/support/session/${sessionId}/close`, { rating });
  return response.data;
});

export const sendSupportMessage = createAsyncThunk('support/sendSupportMessage', async ({ sessionId, message, cannedResponseId, senderId }) => {
  const response = await api.post(`/support/message`, { support_session_id: sessionId, message, canned_response_id: cannedResponseId, sender_id: senderId });
  return response.data;
});

export const fetchPendingRequests = createAsyncThunk('support/fetchPendingRequests', async () => {
  const response = await api.get(`/support/pending`);
  return response.data;
});

export const createSupportSession = createAsyncThunk('support/createSupportSession', async (data) => {
  const response = await api.post(`/support/session`, data);
  return response.data;
});

export const fetchCannedResponses = createAsyncThunk('support/fetchCannedResponses', async () => {
  const response = await api.get(`/support/canned-responses`);
  return response.data;
});

export const createCannedResponse = createAsyncThunk('support/createCannedResponse', async (data) => {
  const response = await api.post(`/support/canned-response`, data);
  return response.data;
});

export const deleteSupportSession = createAsyncThunk('support/session', async ({ sessionId, ender_id }) => {
  const response = await api.delete(`/support/session/${sessionId}`, { data: { ender_id } });
  return { sessionId };
});
export const fetchSupportMessages = createAsyncThunk('support/fetchSupportMessages', async (sessionId) => {
  const response = await api.get(`/support/messages/${sessionId}`);
  return response.data;
});

const supportSlice = createSlice({
  name: 'support',
  initialState: {
    requests: [],
    sessions: [],
    messages: [],
    pendingRequests: [],
    cannedResponses: [],
    autoReply: null,
    loading: false,
    error: null,
    sessionEnded: false,
    unreadMessages: 0,  
  },
  reducers: {
    addMessage: (state, action) => {
      state.messages.push(action.payload);
    },
    addSupportRequest: (state, action) => {
      state.requests.push(action.payload);
    },
    resetSession: (state) => {
      state.sessions = [];
      state.messages = [];
      state.sessionEnded = false;
      state.unreadMessages = 0; // إعادة تعيين الرسائل غير المقروءة
    },
    setSessionEnded: (state) => {
      state.sessionEnded = true;
    },
    incrementUnreadMessages: (state) => {
      state.unreadMessages += 1; // زيادة عدد الرسائل غير المقروءة
    },
    resetUnreadMessages: (state) => {
      state.unreadMessages = 0; // إعادة تعيين عند قراءة الرسائل
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSupportRequests.fulfilled, (state, action) => {
        state.requests = action.payload;
        state.loading = false;
      })
      .addCase(createSupportRequest.fulfilled, (state, action) => {
        state.requests.push(action.payload.supportRequest);
        state.autoReply = action.payload.autoReply;
        state.loading = false;
      })
      .addCase(fetchSupportSessions.fulfilled, (state, action) => {
        state.sessions = action.payload;
        state.loading = false;
      })
      .addCase(closeSupportSession.fulfilled, (state, action) => {
        state.sessions = state.sessions.filter(session => session._id !== action.payload._id);
        state.messages = []; // إعادة تعيين الرسائل
        state.loading = false;
      })
      .addCase(sendSupportMessage.fulfilled, (state, action) => {
        state.messages.push(action.payload);
        state.loading = false;
      })
      .addCase(fetchPendingRequests.fulfilled, (state, action) => {
        state.pendingRequests = action.payload;
        state.loading = false;
      })
      .addCase(createSupportSession.fulfilled, (state, action) => {
        state.sessions.push(action.payload);
        state.pendingRequests = state.pendingRequests.filter(req => req._id !== action.payload.support_request_id);
        state.loading = false;
      })
      .addCase(fetchCannedResponses.fulfilled, (state, action) => {
        state.cannedResponses = action.payload;
        state.loading = false;
      })
      .addCase(createCannedResponse.fulfilled, (state, action) => {
        state.cannedResponses.push(action.payload);
        state.loading = false;
      })
      .addCase(deleteSupportSession.fulfilled, (state, action) => {
        state.sessions = state.sessions.filter(session => session._id !== action.payload.sessionId);
        state.messages = state.messages.filter(msg => msg.support_session_id !== action.payload.sessionId);
        state.loading = false;
        state.sessionEnded = true;
      })
      .addCase(fetchSupportMessages.fulfilled, (state, action) => {
        state.messages = [...state.messages.filter(msg => msg.support_session_id !== action.meta.arg), ...action.payload];
        state.loading = false;
      })
      .addCase(fetchSupportMessages.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSupportMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      .addMatcher(
        (action) => action.type.endsWith('/pending'),
        (state) => { state.loading = true; }
      )
      .addMatcher(
        (action) => action.type.endsWith('/rejected'),
        (state, action) => { state.loading = false; state.error = action.payload || action.error.message; }
      );
  },
});

export const { addMessage, addSupportRequest, resetSession, setSessionEnded ,incrementUnreadMessages ,resetUnreadMessages } = supportSlice.actions;
export default supportSlice.reducer;